/* eslint-disable no-console */
import { isDev } from '@myre/common-conf';
import { LangLocale } from '@myre/common-i18n';
import { isDefined } from '@myre/common-primitives';
import {
  DateFormatPipe,
  LANGLOCALE,
  NumberFormatPipe,
  TRANSLATOR,
  type Translator,
} from '@myre/ui-primitive-core';

import { PercentPipe } from '@angular/common';
import {
  inject,
  Injectable,
  isDevMode,
  type PipeTransform,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  type ValidationErrors,
  type ValidatorFn,
} from '@angular/forms';
import { cloneDeep, compact } from 'lodash-es';
import { type Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { type ElementBase } from '../elements';
import { syncPristineFromChildren } from '../forms.helpers';
import { FORMS_CONFIG, type FormsConfig } from '../providers/forms-config';
import {
  dateMaxValidator,
  dateMinValidator,
  type AmountErrorType,
  type QuestionValidator,
} from '../validators';

import { type Destroyable } from './destroyable.types';
import { QuestionBase } from './question-base';
import { QuestionContainer } from './question-container';
import { QuestionControl } from './question-control';
import { QuestionDate, type DateErrorType } from './question-date';
import { QuestionFinancialValue } from './question-financial-value';
import { QuestionGroup } from './question-group';
import { QuestionHidden } from './question-hidden';
import { QuestionMultiBase } from './question-multi-base';
import { QuestionMultiSelect } from './question-multi-select';
import { QuestionNumber } from './question-number';
import { type QuestionUpload } from './question-upload';
import { isQuestionContainer } from './question.helpers';

const _runValidator = (AbstractControl.prototype as any)._runValidator;

(AbstractControl.prototype as any)._runValidator = function (
  ...args: any[]
): any {
  const ret = _runValidator.bind(this)(...args);

  (this as { warnings: ValidationErrors | null }).warnings = this
    .warningValidator
    ? this.warningValidator(this)
    : null;
  (this as { info: ValidationErrors | null }).info = this.infoValidator
    ? this.infoValidator(this)
    : null;

  return ret;
};
(AbstractControl.prototype as any).setWarnings = function (
  warnings: ValidationErrors | null,
): void {
  (this as { warnings: ValidationErrors | null }).warnings = warnings;
};
(AbstractControl.prototype as any).setInfo = function (
  info: ValidationErrors | null,
): void {
  (this as { info: ValidationErrors | null }).info = info;
};
(UntypedFormArray.prototype as any)._allControlsDisabled = (
  UntypedFormControl.prototype as any
)._allControlsDisabled;

let NEXT_SUB_WARN_STEP = 50;

const libraryValidatorPrefix = '_library.validators';

@Injectable({ providedIn: 'root' })
export class QuestionControlService {
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);
  private _formsConfig = inject<FormsConfig>(FORMS_CONFIG, { optional: true });
  private _translator =
    inject<Translator>(TRANSLATOR, { optional: true }) ?? (key => key);

  private _dateFormatPipe = inject(DateFormatPipe);
  private _numberFormatPipe = inject(NumberFormatPipe);
  private _percentPipe = inject(PercentPipe);

  private _formSubscriptions: Map<AbstractControl, Subscription[]> = new Map<
    AbstractControl,
    Subscription[]
  >();

  private _skipBuildElements: Map<ElementBase, boolean> = new Map<
    ElementBase,
    boolean
  >();

  private _cachedCollapseStatus: Map<string, boolean | boolean[]> = new Map<
    string,
    boolean | boolean[]
  >();

  // see question.onValueChange(.. for explanations
  private _breadcrumb: ElementBase[] = [];

  public getCachedCollapseStatus(key: string): boolean | boolean[] {
    return this._cachedCollapseStatus.get(key) ?? null;
  }

  public setCachedCollapseStatus(
    key: string,
    value: boolean | boolean[],
  ): void {
    this._cachedCollapseStatus.set(key, value);
  }

  public clearCachedCollapseStatus(): void {
    this._cachedCollapseStatus.clear();
  }

  /**
   * @param rootForm the page's top-level form. Passed by a container when it
   *   builds its children's sub-form (see `QuestionContainer.initItem`), so
   *   those children can flip the real root's dirty state — Angular detaches
   *   the sub-form from the root at render time, breaking the usual bubbling.
   *   Defaults to the form being generated (top-level case).
   */
  public generateForm(
    elements: ElementBase[],
    rootForm?: AbstractControl,
  ): UntypedFormGroup {
    const formGroup = this._toFormGroup(elements);
    const elementsMap = this._getElementsMap(elements);

    this._handleFormHooks(
      elements,
      elementsMap,
      formGroup,
      true,
      rootForm ?? formGroup,
    );
    this._callHooks(elements, elementsMap);

    return formGroup;
  }

  public toValues(
    elements: ElementBase[] = [],
    keepHidden: boolean = false,
  ): any {
    const newValues: any = {};
    const questions = elements.filter(
      element =>
        element instanceof QuestionBase &&
        (keepHidden || !(element instanceof QuestionHidden)),
    ) as QuestionBase<any>[];

    for (const question of questions) {
      const questionValue = question.getValue(keepHidden);

      if (question.ascendValuesToRoot) {
        for (const key of Object.keys(questionValue)) {
          newValues[key] = questionValue[key];
        }
      } else {
        if (question instanceof QuestionMultiBase && question.trackBy) {
          newValues[question.key] = this._getTrackByValue(
            question,
            questionValue,
          );
        } else {
          newValues[question.key] = questionValue;
        }
      }
    }

    return newValues;
  }

  public hasUpperLabel(element: ElementBase): boolean {
    if (!(element instanceof QuestionBase)) {
      return false;
    }

    if (element instanceof QuestionGroup) {
      if (element.collapsable) {
        return !!element.label;
      }

      return (
        (element.showIndex && !!element.getValue().length) ||
        !!element.getLabel(0) ||
        !!element.label
      );
    }

    if (
      element instanceof QuestionContainer ||
      element instanceof QuestionMultiSelect
    ) {
      return !!element.label;
    }

    return (
      !['checkbox', 'switch', 'slider'].includes(element.elementType) &&
      (element.elementType !== 'upload' ||
        (element as QuestionUpload).design !== 'button')
    );
  }

  public destroy(form: AbstractControl): void {
    if (!form) {
      return;
    }

    const n = this._destroy(form);

    if (isDevMode()) {
      let m = 0;

      this._formSubscriptions.forEach(v => {
        m += v.length;
      });

      if (m >= NEXT_SUB_WARN_STEP) {
        NEXT_SUB_WARN_STEP += 50;
        console.warn(
          `${n} control${
            n > 1 ? 's' : ''
          } destroyed but total of ${m} Subscription${
            m > 1 ? 's' : ''
          } remaining!`,
        );
      }
    }
  }

  public dateErrorFormatter(date: Date, type: DateErrorType): string {
    switch (type) {
      case '>': {
        return this._translator(`${libraryValidatorPrefix}.dateTooHigh`, {
          date: this._dateFormatPipe.transform(date),
        });
      }
      case '<': {
        return this._translator(`${libraryValidatorPrefix}.dateTooLow`, {
          date: this._dateFormatPipe.transform(date),
        });
      }
    }
  }

  public financialErrorFormatter(
    value: number,
    type: AmountErrorType,
    currencyPipe: PipeTransform,
  ): string {
    return this._numberErrorFormatter(value, type, 'financial', currencyPipe);
  }

  public percentErrorFormatter(value: number, type: AmountErrorType): string {
    return this._numberErrorFormatter(
      value,
      type,
      'percent',
      this._percentPipe,
    );
  }

  public valueErrorFormatter(value: number, type: AmountErrorType): string {
    return this._numberErrorFormatter(value, type, 'value');
  }

  private _numberErrorFormatter(
    amount: number,
    type: AmountErrorType,
    kind: 'value' | 'financial' | 'percent',
    pipe?: PipeTransform,
  ): string {
    const isFinancial = kind === 'financial';
    const isPercent = kind === 'percent';

    if (amount === 0) {
      switch (type) {
        case '<': {
          return this._translator(
            `${libraryValidatorPrefix}.${
              isFinancial ? 'amount' : 'value'
            }ShouldBePositive`,
          );
        }
        case '>': {
          return this._translator(
            `${libraryValidatorPrefix}.${
              isFinancial ? 'amount' : 'value'
            }ShouldBeNegative`,
          );
        }
        case '<=': {
          return this._translator(
            `${libraryValidatorPrefix}.${
              isFinancial ? 'amount' : 'value'
            }ShouldBeStrictlyPositive`,
          );
        }
      }
    }

    if (isFinancial || isPercent) {
      amount /= 100;
    }

    const formattedAmount = this._formatAmount(amount, isPercent, pipe);

    switch (type) {
      case '>': {
        return this._translator(`${libraryValidatorPrefix}.amountTooBig`, {
          amount: formattedAmount,
        });
      }
      case '<': {
        return this._translator(`${libraryValidatorPrefix}.amountTooLow`, {
          amount: formattedAmount,
        });
      }
    }
  }

  /**
   * @returns the formatted amount depending on type and pipe:
   * - if amount is of type percent, the pipe is used to format the amount
   * - if the pipe is provided and the amount is not percent, pipe is used to format the amount, with the numberFormatPipe
   * - if no pipe is provided, numberFormatPipe is used
   *
   * In case of percentage value, pipe must be provided
   */
  private _formatAmount(
    amount: number,
    isPercent: boolean,
    pipe?: PipeTransform,
  ): string {
    if (isPercent) {
      if (isDev() && !pipe) {
        throw new Error('Pipe must be provided for percentage values');
      }

      return pipe.transform(amount);
    }

    const formattedAmount = this._numberFormatPipe.transform(
      amount,
      1,
      '1.0-2',
    );

    return pipe ? pipe.transform(formattedAmount) : formattedAmount;
  }

  private _destroy(control: AbstractControl): number {
    const subscriptions = this._formSubscriptions.get(control);
    let n = 0;

    if (
      control instanceof UntypedFormGroup ||
      control instanceof UntypedFormArray
    ) {
      n += Object.values(control.controls).reduce(
        (s, c) => s + this._destroy(c),
        0,
      );
    }

    (control as unknown as Destroyable).destroy?.();

    if (subscriptions) {
      n += subscriptions.length;
      subscriptions.forEach(s => s.unsubscribe());
      this._formSubscriptions.delete(control);
    }

    return n;
  }

  private _toFormGroup(elements: ElementBase[]): UntypedFormGroup {
    const form = new UntypedFormGroup({});

    if (elements.some(el => typeof el.order !== 'number')) {
      for (let i = 0; i < elements.length; i++) {
        elements[i].order = i;
      }
    }

    elements.sort((a, b) => a.order - b.order);

    const questions = elements.filter(
      element => element instanceof QuestionBase,
    );

    for (const question of questions) {
      if (question.skipBuildValueChanges) {
        this._skipBuildElements.set(question, true);
      }

      question.questionControlService = this;
      question.lang = this._langLocale;
      question.initStart();

      let control: AbstractControl;

      if (
        question instanceof QuestionGroup ||
        question instanceof QuestionContainer
      ) {
        control = new UntypedFormArray([]);
      } else {
        control = new QuestionControl(null, {
          updateOn: question.updateOn,
        });
      }

      form.registerControl(question.key, control);
      question.registerFormControl(control);
    }

    for (const question of questions) {
      question.setValue(
        cloneDeep(
          question.initialValue !== undefined
            ? question.initialValue
            : question.defaultValue,
        ),
      );

      let initialValue = question.initialValue;

      if (question instanceof QuestionGroup) {
        initialValue = initialValue === undefined ? [] : initialValue;
      }

      if (
        (question instanceof QuestionNumber ||
          question instanceof QuestionFinancialValue) &&
        isDefined(this._formsConfig?.minNumber) &&
        !isDefined(question.min)
      ) {
        question.min = this._formsConfig.minNumber;
      }

      if (
        (question instanceof QuestionNumber ||
          question instanceof QuestionFinancialValue) &&
        isDefined(this._formsConfig?.maxNumber) &&
        !isDefined(question.max)
      ) {
        question.max = this._formsConfig.maxNumber;
      }

      if (question instanceof QuestionDate) {
        if (this._formsConfig?.minDate && !question.options?.min) {
          question.setOptions(
            { ...question.options, min: this._formsConfig.minDate },
            false,
          );
        }

        if (this._formsConfig?.maxDate && !question.options?.max) {
          question.setOptions(
            { ...question.options, max: this._formsConfig.maxDate },
            false,
          );
        }

        // Add default validators for date range
        question.validators = compact([
          ...question.validators,
          ...[
            {
              validator: dateMinValidator,
              dateLimit: this._formsConfig?.minDate,
              compare: '<',
            },
            {
              validator: dateMaxValidator,
              dateLimit: this._formsConfig?.maxDate,
              compare: '>',
            },
          ].map(({ validator, dateLimit, compare }) =>
            validator(dateLimit, 'd', date =>
              this.dateErrorFormatter(date, compare as DateErrorType),
            ),
          ),
        ]);
      }

      if (question.equals(initialValue)) {
        question.getFormControl().markAsPristine();
      } else {
        question.getFormControl().markAsDirty();
      }

      if (question.initialDisabled) {
        question.disable(question.initialKeepAtPositionOnDisabled);
      } else {
        question.enable();
      }
    }

    syncPristineFromChildren(form);

    for (const question of questions) {
      const control = question.getFormControl();

      // We should be using question.setValidators instead ; control.setValidators should only be called internally by Angular
      control.setValidators = function (): void {};

      if (question.validators && question.validators.length) {
        control.validator = this._getValidatorFn(
          question,
          elements,
          question.validators,
        );
      }

      if (question.warningValidators && question.warningValidators.length) {
        (control as any).warningValidator = this._getValidatorFn(
          question,
          elements,
          question.warningValidators,
        );
      }

      if (question.infoValidators && question.infoValidators.length) {
        (control as any).infoValidator = this._getValidatorFn(
          question,
          elements,
          question.infoValidators,
        );
      }

      question.registerSetValidators(validators => {
        control.validator = this._getValidatorFn(
          question,
          elements,
          validators,
        );
      });
      question.registerSetWarningValidators(validators => {
        (control as any).warningValidator = this._getValidatorFn(
          question,
          elements,
          validators,
        );
      });
      question.update();
      const value = question.getValue();

      // Errors and warnings are displayed only in the presence of a value
      // Info are always displayed
      if (
        (isDefined(value) && (control.errors || (control as any).warnings)) ||
        (control as any).info
      ) {
        control.markAsTouched({ onlySelf: true });
      }
    }

    for (const element of elements) {
      element.initEnd();
    }

    (form as any).__isMyreForm = true;

    return form;
  }

  private _handleFormHooks(
    elements: ElementBase[],
    elementsMap: Map<string, ElementBase>,
    form: AbstractControl,
    root: boolean = false,
    trueRoot: AbstractControl = form,
  ): void {
    this._subscribe(
      form,
      form.valueChanges.subscribe(() => {
        const values = this.toValues(elements);

        if (root) {
          this._handleRootChange(values, elements, elementsMap);
        }

        elements
          .filter(
            element =>
              (element instanceof QuestionBase && !element.disabled) ||
              !(element instanceof QuestionBase),
          )
          .forEach((element: ElementBase) => {
            // see question.onValueChange(.. for explanations
            const initiator = this._breadcrumb[this._breadcrumb.length - 1];
            this._breadcrumb.push(element);
            element.onChange(values, elementsMap, element, initiator);
            this._breadcrumb.pop();
          });
      }),
    );

    const questions = elements.filter(
      element => element instanceof QuestionBase,
    );

    questions.forEach((question: QuestionBase<any>) => {
      let lastValue: any = question.getValue();

      if (question.value$) {
        const subscriptionToValue = question.value$
          .pipe(filter(value => !question.equals(value)))
          .subscribe(value => question.setValue(value));

        this._subscribe(question.getFormControl(), subscriptionToValue);
      }

      this._subscribe(
        question.getFormControl(),
        question.getFormControl().valueChanges.subscribe(() => {
          if (!question.disabled) {
            if (question.equals(question.initialValue)) {
              question.getFormControl().markAsPristine();
            } else {
              question.getFormControl().markAsDirty();

              // A QuestionContainer builds its children in a sub-form that
              // Angular's reactive directives detach from the page's root
              // form at render time, so the child's dirty never bubbles up.
              // Mark the real root (threaded in via `generateForm`) dirty so
              // the Save button reacts to container-field edits.
              if (trueRoot !== question.getFormControl()) {
                trueRoot.markAsDirty();
              }
            }

            if (
              !question.equals(lastValue) &&
              (!question.skipBuildValueChanges ||
                !this._skipBuildElements.has(question))
            ) {
              lastValue = question.getValue();

              // this._breadcrumb represents the chain of elements which hooks (onValueChange, onChange, onRootChange and validators)
              // are called on.
              // we are just going to call the hook onValueChange on the question Q bellow
              // JUST BEFORE calling onValueChange we will push Q in the breadcrumb
              // JUST AFTER calling onValueChange we will pop the last element of the breadcrumb
              // (which will be Q)
              // this way, the last element of this._breadcrumb is always the last element which
              // a hook is called on, even if an element A calls setValue on an
              // element B which calls setValue on an element C anc so on
              // notice that this can only work if setValue is called synchronously within the hook
              // when the chain ends, this._breadcrumb should be [].
              // if initiator is undefined it may mean that the hook is called:
              //  - on initial hook call
              //  - via a direct user input
              //  - a setValue outside a hook
              const initiator = this._breadcrumb[this._breadcrumb.length - 1];
              this._breadcrumb.push(question);
              question.onValueChange(
                question.getValue(),
                elementsMap,
                question,
                initiator,
              );
              this._validateLinkedQuestions(question, elementsMap);
              this._breadcrumb.pop();
            }
          }
        }),
      );
    });
  }

  private _handleRootChange(
    values: any,
    elements: ElementBase[],
    elementsMap: Map<string, ElementBase>,
  ): void {
    elements
      .filter(
        element =>
          (element instanceof QuestionBase && !element.disabled) ||
          !(element instanceof QuestionBase),
      )
      .forEach((element: ElementBase) => {
        const initiator = this._breadcrumb[this._breadcrumb.length - 1];
        this._breadcrumb.push(element);
        element.onRootChange(values, elementsMap, element, initiator);
        this._breadcrumb.pop();

        if (element instanceof QuestionGroup) {
          for (const groupElement of element.elements) {
            this._handleRootChange(values, groupElement, elementsMap);
          }
        } else if (element instanceof QuestionContainer) {
          this._handleRootChange(values, element.elements, elementsMap);
        }
      });
  }

  private _callHooks(
    elements: ElementBase[],
    elementsMap: Map<string, ElementBase>,
  ): void {
    for (const element of elements) {
      if (
        (element instanceof QuestionBase && !element.disabled) ||
        !(element instanceof QuestionBase)
      ) {
        const initiator = this._breadcrumb[this._breadcrumb.length - 1];
        this._breadcrumb.push(element);
        element.onChange(
          this.toValues(elements),
          elementsMap,
          element,
          initiator,
        );
        this._breadcrumb.pop();
      }

      if (
        element instanceof QuestionBase &&
        !element.disabled &&
        !element.skipInitialValueChange
      ) {
        const initiator = this._breadcrumb[this._breadcrumb.length - 1];
        this._breadcrumb.push(element);
        element.onValueChange(
          element.getValue(),
          elementsMap,
          element,
          initiator,
        );
        this._breadcrumb.pop();
      }

      if (element instanceof QuestionGroup) {
        for (let i = 0; i < element.elements.length; i++) {
          const elementGroupMap = this._getElementsMap(element.elements[i]);

          this._callHooks(element.elements[i], elementGroupMap);
        }
      } else if (element instanceof QuestionContainer) {
        const elementGroupMap = this._getElementsMap(element.elements);

        this._callHooks(element.elements, elementGroupMap);
      }
    }

    for (const element of elements) {
      this._skipBuildElements.delete(element);
    }
  }

  private _subscribe(form: AbstractControl, subscription: Subscription): void {
    if (!this._formSubscriptions.has(form)) {
      this._formSubscriptions.set(form, []);
    }

    const subs = this._formSubscriptions.get(form);

    if (subs) {
      subs.push(subscription);
    }
  }

  private _getElementsMap(elements: ElementBase[]): Map<string, ElementBase> {
    const map = new Map<string, ElementBase>();

    for (const element of elements) {
      map.set(element.key, element);
    }

    return map;
  }

  private _getValidatorFn(
    question: QuestionBase<any>,
    elements: ElementBase[],
    validators: QuestionValidator[],
  ): ValidatorFn {
    let fake = new UntypedFormControl();

    fake.setValidators(
      validators.map(validator => {
        if (!validator) {
          throw new Error('Provided a null or undefined validator.');
        }

        return this._proxyValidator(question, elements, validator);
      }),
    );

    const validatorFn = fake.validator;
    fake = null;

    return validatorFn;
  }

  private _proxyValidator(
    question: QuestionBase<any>,
    elements: ElementBase[],
    validator: QuestionValidator,
  ): ValidatorFn {
    return control => {
      const initiator = this._breadcrumb[this._breadcrumb.length - 1];
      this._breadcrumb.push(question);
      const r = validator(
        control,
        question.getValue(true),
        this._getElementsMap(elements),
        question,
        initiator,
      );
      this._breadcrumb.pop();

      return r;
    };
  }

  /**
   * Run validation for linkedQuestions.
   *
   * Linked question can be direct sibling or a composite key to access question inside a question container.
   * Composite key can only have one level of depth
   *
   * @example
   * ```
   * const elements = [
   *   new QuestionTextbox({
   *     key: 'qt1',
   *     linkedQuestions: ['container.qt2'],
   *   }),
   *   new QuestionContainer({
   *     key: 'container',
   *     elements: [
   *       new QuestionTextbox({
   *         key: 'qt2',
   *       }),
   *     ],
   *   });
   * ```
   */
  private _validateLinkedQuestions(
    question: QuestionBase<any>,
    elementsMap: Map<string, ElementBase>,
  ): void {
    for (const key of question.linkedQuestions) {
      const isCompositeKey = key.includes('.');

      const keyParts = key.split('.');

      let el = elementsMap.get(keyParts[0]) as QuestionBase<any>;

      if (!el) {
        continue;
      }

      if (isCompositeKey) {
        if (!isQuestionContainer(el)) {
          if (isDevMode()) {
            throw new Error(
              "Can't access inner elements if question is not a QuestionContainer",
            );
          }

          continue;
        }

        el = el.elements.find(e => e.key === keyParts[1]) as QuestionBase<any>;

        if (!el) {
          continue;
        }
      }

      const value = el.getValue();
      el.validate(isDefined(value), false);
    }
  }

  private _getTrackByValue(element: QuestionMultiBase, elementValue: any): any {
    if (Array.isArray(elementValue)) {
      const ids = elementValue.map(e => element.trackBy(e));

      return element.optionsSync
        .filter(v => v.value && ids.includes(element.trackBy(v.value)))
        .map(v => v.value);
    }

    if (isDefined(elementValue)) {
      const id = element.trackBy(elementValue);

      return element.optionsSync.find(
        v => v.value && id === element.trackBy(v.value),
      )?.value;
    }

    return elementValue;
  }
}
